import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';
import { PanelLeft, Globe, LayoutDashboard, Database, Wrench, LogOut, Loader2, RefreshCw, Sun, Moon, MessageSquare, Settings, Users, Layers, Play, History, Bot, ChevronLeft, ChevronRight, Target, BarChart2 } from 'lucide-react';

import api from './api';
import { useScraperStatus } from './hooks/useScraperStatus';
import { useLeadSelection } from './hooks/useLeadSelection';
import { useAgent2 } from './hooks/useAgent2';
import { useAgent2Queue } from './hooks/useAgent2Queue';
import { useAgent3 } from './hooks/useAgent3';
import { usePricing, calcPricing } from './hooks/usePricing';
import { useStripe } from './hooks/useStripe';

import { useUIStore } from './stores/useUIStore';
import { useFilterStore } from './stores/useFilterStore';
import { useConfigStore } from './stores/useConfigStore';

const AdminDashboardOverview = lazy(() => import('./components/AdminDashboardOverview'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const FlowConfig = lazy(() => import('./components/FlowConfig'));
const ExtractionHistory = lazy(() => import('./components/ExtractionHistory'));
const AdminLeadsTable = lazy(() => import('./components/AdminLeadsTable'));
const ClientLeadsView = lazy(() => import('./components/ClientLeadsView'));
const Agent2History = lazy(() => import('./components/Agent2History'));
const Agent2RetroQueue = lazy(() => import('./components/Agent2RetroQueue'));
const Agent3History = lazy(() => import('./components/Agent3History'));
const AdminRequests = lazy(() => import('./components/AdminRequests'));
const UserProfileSettings = lazy(() => import('./components/UserProfileSettings'));
const AdminClientsTable = lazy(() => import('./components/AdminClientsTable'));
const AIAgentsCenter = lazy(() => import('./components/AIAgentsCenter'));
import Modal from './components/Modal';
import ValidationReportModal from './components/ValidationReportModal';
import ContractModal from './components/ContractModal';
import AdminSidebar from './layouts/AdminSidebar';

export default function App() {
  const navigate = useNavigate();
  const { user, userProfile, userRole: firebaseRole, loading: authLoading, logout: firebaseLogout } = useAuth();
  const loggedIn = !!user;
  const userRole = firebaseRole || 'client';
  const isAdmin = userRole === 'admin';

  // Scraper hook
  const {
    isRunning, isPaused, status, results, setResults, resultsLoading,
    syncResults, startScraping, stopScraping, pauseScraping, resumeScraping, clearResults, resetLogs,
  } = useScraperStatus();

  // Stripe hook
  const { createCheckout, isProcessing: stripeProcessing } = useStripe();

  // Agent2 hook
  const { agent2History, agent2Running, agent2HistoryError, fetchAgent2History, validateBase } = useAgent2();

  // Agent2 Queue hook
  const { agent2Status, agent2QueueHistory, fetchAgent2QueueHistory, startAgent2Queue, stopAgent2Queue } = useAgent2Queue();

  // Agent3 hook
  const { agent3History, agent3Status, agent3Error, fetchAgent3History, startAgent3Queue, stopAgent3Queue } = useAgent3();

  // Filter state
  const {
    resultSearchInput, setResultSearchInput,
    categoryFilter, setCategoryFilter,
    sourceFilter, setSourceFilter,
    resultStateFilter, setResultStateFilter,
    contactFilter, setContactFilter
  } = useFilterStore();

  const {
    theme, toggleTheme,
    adminSidebarExpanded: sidebarExpanded, 
    toggleAdminSidebar: toggleSidebar,
    activeDashboardSection, setActiveDashboardSection,
    confirmModal, setConfirmModal,
    baseValidationReport, setBaseValidationReport
  } = useUIStore();

  // Search config state
  const {
    searchMode, setSearchMode,
    categories, setCategories,
    inputValue, setInputValue,
    dripMode, setDripMode,
    totalGoal, setTotalGoal,
    hours, setHours,
    waitMinutes, setWaitMinutes,
    regionState, setRegionState,
    country, setCountry,
    selectedStates, setSelectedStates,
    statePicker, setStatePicker,
    searchSequence, setSearchSequence,
    searchSequenceInput, setSearchSequenceInput,
    seqCity, setSeqCity,
    seqNeighborhood, setSeqNeighborhood,
    seqCoords, setSeqCoords,
    seqRadius, setSeqRadius,
    extractSocials, setExtractSocials,
    enableAgent3, setEnableAgent3,
    headlessMode, setHeadlessMode,
    enterpriseType, setEnterpriseType,
    panelDrip, setPanelDrip,
    panelRegion, setPanelRegion,
    panelQueue, setPanelQueue,
    hydrateFromPayload
  } = useConfigStore();

  const [apiKey, setApiKey] = useState(localStorage.getItem('leadszonemap_api_key') || '');


  const activeResults = results.filter(r => r.Estado !== 'Excluído');
  const excludedResults = results.filter(r => r.Estado === 'Excluído');

  const normalizedSearch = resultSearchInput.trim().toLowerCase();
  const availableCategories = Array.from(new Set(activeResults.map((r) => r.Categoria).filter(Boolean))).sort();
  const availableSources = Array.from(new Set(activeResults.map((r) => r.Fonte).filter(Boolean))).sort();
  const availableResultStates = Array.from(new Set(activeResults.map((r) => r.Estado).filter(Boolean))).sort();

  console.log("App leads debug:", {
    total_results: results.length,
    active_results: activeResults.length,
    available_categories: availableCategories,
    category_filter: categoryFilter,
    result_state_filter: resultStateFilter,
  });

  const filteredResults = activeResults.filter((item) => {
    if (categoryFilter && categoryFilter.length > 0 && !categoryFilter.includes(item.Categoria)) return false;
    if (sourceFilter !== 'all' && item.Fonte !== sourceFilter) return false;
    if (resultStateFilter !== 'all' && item.Estado !== resultStateFilter) return false;
    if (contactFilter !== 'all') {
      if (contactFilter === 'whatsapp') {
        const hasPhone = Boolean(
          (item.WhatsApp && item.WhatsApp !== 'N/A') || 
          (item.Telefone && item.Telefone !== 'N/A')
        );
        if (!hasPhone) return false;
      } else if (contactFilter === 'email') {
        const hasEmail = Boolean(
          (Array.isArray(item.Emails) && item.Emails.length > 0) || 
          (typeof item.Email === 'string' && item.Email.includes('@'))
        );
        if (!hasEmail) return false;
      } else {
        const channels = Array.isArray(item.Canais) ? item.Canais : [];
        if (!channels.includes(contactFilter)) return false;
      }
    }
    if (!normalizedSearch) return true;
    const searchable = [
      item.Nome, item.Categoria, item.Endereco, item.Busca,
      item.SequenciaBusca, item.Estado, item.Fonte,
      ...(Array.isArray(item.PalavrasChave) ? item.PalavrasChave : []),
      ...(Array.isArray(item.Tags) ? item.Tags : []),
    ].join(' ').toLowerCase();
    return searchable.includes(normalizedSearch);
  });

  // Lead selection (client mode)
  const leadSelection = useLeadSelection(filteredResults);

  // Pricing
  const { downloadPricingCSV } = usePricing(leadSelection.leadCartCount);

  // Settings sync from SettingsPage
  useEffect(() => {
    const sync = () => {
      setApiKey(localStorage.getItem('leadszonemap_api_key') || '');
      setEnterpriseType(localStorage.getItem('leadszonemap_enterprise_type') || 'all');
    };
    window.addEventListener('leadszonemap-settings-updated', sync);
    sync();
    return () => window.removeEventListener('leadszonemap-settings-updated', sync);
  }, []);

  // Results polling
  const handleManualSync = () => syncResults(isAdmin, true);

  useEffect(() => {
    if (!loggedIn) return;
    
    // Sincroniza uma vez na montagem da tela ou ao entrar
    handleManualSync();
    
    // Atualização reativa baseada em WebSockets (tempo real)
    const handleBatchComplete = () => {
      handleManualSync();
    };

    window.addEventListener('leadszonemap-batch-complete', handleBatchComplete);
    
    return () => {
      window.removeEventListener('leadszonemap-batch-complete', handleBatchComplete);
    };
  }, [loggedIn, isAdmin, syncResults]);

  useEffect(() => {
    if (loggedIn && isAdmin) {
      fetchAgent2History();
      fetchAgent2QueueHistory();
      fetchAgent3History();
    }
  }, [loggedIn, isAdmin, fetchAgent2History, fetchAgent2QueueHistory, fetchAgent3History]);

  // Hydrate state from active engine payload
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    if (isRunning && status?.payload && !hasHydrated) {
      hydrateFromPayload(status.payload);
      setHasHydrated(true);
    }
  }, [isRunning, status, hasHydrated, hydrateFromPayload]);

  const handleLogout = useCallback(async () => {
    await firebaseLogout();
    navigate('/');
  }, [firebaseLogout, navigate]);

  // Screen switching (no scroll — each section is a full screen)

  // Search handlers
  const handleStart = async () => {
    resetLogs();
    
    if (!apiKey) {
      alert("⚠️ ERRO: Falta a Chave de API! Por favor, vá na aba 'Configurações' (⚙️) no painel e insira sua Google Maps API Key antes de iniciar a extração.");
      return;
    }

    // Convert advanced search objects to strings "key:value" or "value"
    let mappedSequence = null;
    if (searchMode === 'avancado' && searchSequence.length) {
      mappedSequence = searchSequence.map(s => {
        if (typeof s === 'object' && s !== null) {
          return s.type ? `${s.type}:${s.val}` : s.val;
        }
        return String(s);
      });
    }

    try {
      await startScraping({
        categories: searchMode === 'fila' ? categories : [],
        api_key: apiKey || null,
        limit: 20,
        drip_mode: dripMode,
        headless: headlessMode,
        total_goal: parseInt(totalGoal, 10) || 100,
        hours: parseInt(hours, 10) || 12,
        state: regionState || null,
        states: selectedStates.length ? selectedStates : null,
        wait_minutes: waitMinutes ? parseInt(waitMinutes, 10) || null : null,
        enterprise_type: enterpriseType,
        country: country.trim() || 'Brasil',
        search_sequence: mappedSequence,
        search_options: { extract_socials: extractSocials, enable_agent3: enableAgent3 }
      });
    } catch (err) {
      const msg = err.response?.data?.detail 
        ? JSON.stringify(err.response.data.detail)
        : err.message;
      alert(`Erro ao iniciar extração: ${msg}. Verifique se todos os campos estão preenchidos corretamente (ex: API Key válida, números válidos).`);
    }
  };

  // Enviar alterações de opções ao backend caso esteja pausado
  useEffect(() => {
    if (isRunning && isPaused) {
      api.post('/update_options', {
        search_options: { extract_socials: extractSocials, headless: headlessMode, enable_agent3: enableAgent3 }
      }).catch(err => console.warn(err));
    }
  }, [extractSocials, headlessMode, enableAgent3, isRunning, isPaused]);

  const handleStartExtractionFromRequest = (req) => {
    setInputValue(req.segmento);
    setSeqCity(req.cidade || '');
    if (req.estado) {
      setSelectedStates([req.estado]);
    } else {
      setSelectedStates([]);
    }
    setSearchMode(req.cidade ? 'avancado' : 'fila');
    setActiveDashboardSection('flow-config');
    toast.success('Robô pré-configurado com a solicitação do cliente!');
  };

  const handleDownload = async () => {
    const res = await api.get('/exports/workbook.xlsx', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `leadszonemap_exports_por_dia_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
      // Refresh agent2 & agent3 stats via API to clear out old data locally
    setTimeout(() => {
        fetchAgent2History();
        fetchAgent2QueueHistory();
        fetchAgent3History();
    }, 3500);
  };

  const handleValidateBase = async () => {
    const report = await validateBase();
    setBaseValidationReport(report);
  };

  const handleClearResults = () => {
    setConfirmModal({
      message: 'Deseja realmente limpar todos os resultados da tela?',
      onConfirm: async () => {
        await clearResults();
        toast.success('Resultados limpos com sucesso!');
      }
    });
  };

  // Auth guard
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem', background: 'var(--bg-main)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div className="client-topbar__logo-dot logo-pulse" style={{ width: 28, height: 28 }} />
          <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.03em' }}>LeadsZone<span style={{color: 'var(--accent)'}}>Map</span></h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <Loader2 size={36} className="spin" style={{ color: 'var(--accent)' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 500, letterSpacing: '0.02em' }}>Autenticando acesso...</p>
        </div>
      </div>
    );
  }
  if (!loggedIn) return <Navigate to="/login" replace />;

  // Client view
  if (!isAdmin) {
    if (resultsLoading) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem', background: 'var(--bg-main)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="client-topbar__logo-dot logo-pulse" style={{ width: 28, height: 28 }} />
            <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.03em' }}>LeadsZone<span style={{color: 'var(--accent)'}}>Map</span></h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%', maxWidth: '800px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 500, letterSpacing: '0.02em', alignSelf: 'center' }}>Carregando sua base de leads exclusiva&hellip;</p>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '1rem' }}>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px' }}></div>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px', opacity: 0.8 }}></div>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px', opacity: 0.6 }}></div>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px', opacity: 0.4 }}></div>
            </div>
          </div>
          <button onClick={handleLogout} style={{ marginTop: '1rem', background: 'transparent', border: '1px solid var(--glass-panel-border)', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', fontSize: '0.9rem', color: 'var(--text-secondary)', borderRadius: '30px', cursor: 'pointer', transition: 'all 0.2s' }}>
            <LogOut size={16} /> Sair
          </button>
        </div>
      );
    }

    return (
      <ClientLeadsView
        status={status}
        results={activeResults}
        filteredResults={filteredResults}
        {...leadSelection}
        availableCategories={availableCategories}
        availableSources={availableSources}
        availableResultStates={availableResultStates}
        onAddToWallet={() => {
          try { leadSelection.addSelectionToLeadWallet(); }
          catch (e) { toast.error(e.message); }
        }}
        onClearSelection={leadSelection.clearLeadSelection}
        onCheckout={async () => {
          try {
            const pricing = calcPricing(leadSelection.leadCartCount);
            const orderId = await leadSelection.confirmLeadWalletCheckout(pricing.packageName);
            toast.success(`Pedido criado! ID: ${orderId}. Redirecionando para pagamento...`);
            
            const totalCents = Math.round(pricing.total * 100);

            await createCheckout({
              exportId: orderId,
              quantity: 1,
              priceCents: totalCents,
              email: user.email,
              successUrl: `${window.location.origin}/dashboard?payment=success`,
              cancelUrl: `${window.location.origin}/dashboard?payment=cancelled`
            });
          } catch (e) { toast.error(e.message); }
        }}
        onLogout={handleLogout}
        onLeadCheckboxClick={leadSelection.handleLeadCheckboxClick}
        setLeadSelectionMap={leadSelection.setLeadSelectionMap}
        onDownloadPricingCSV={downloadPricingCSV}
      />
    );
  }

  // Admin view
  const renderAdminScreen = () => {
    switch (activeDashboardSection) {
      case 'config':
        return (
          <div className="admin-screen animate-fade-in">
            <Suspense fallback={<div className="client-dashboard__loading"><Loader2 size={32} className="animate-spin" /></div>}>
              <FlowConfig
                isRunning={isRunning} isPaused={isPaused}
                apiKey={apiKey} setApiKey={setApiKey}
                status={status} results={activeResults} agent2Running={agent2Running}
                onStart={handleStart}
                onStop={stopScraping}
                onPause={pauseScraping}
                onResume={resumeScraping}
                onDownload={handleDownload}
                onValidateBase={handleValidateBase}
              />

            <div className="config-section">
              <div className="section-header">
                <Layers size={18} />
                <h3>Higienização de Base</h3>
              </div>
              <div className="flow-card">
                <div className="flow-card-header">
                  <div className="flow-card-icon" style={{ background: 'rgba(116,242,255,0.1)' }}>
                    <Layers size={18} color="var(--accent)" />
                  </div>
                  <div className="flow-card-title">
                    <h4>Higienização de Base</h4>
                    <p>Limpeza, formatação e validação</p>
                  </div>
                  <div className="flow-card-actions">
                    <button
                      onClick={handleValidateBase}
                      disabled={agent2Running}
                      className="btn-glass"
                    >
                      {agent2Running ? <Loader2 size={14} className="spin" /> : <Play size={14} />}
                      {agent2Running ? " Limpando..." : " Limpar Base"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <ExtractionHistory />

            <Agent2History 
              agent2History={agent2History} 
              agent2HistoryError={agent2HistoryError} 
              onRefresh={fetchAgent2History} 
            />
              <Agent2RetroQueue agent2QueueHistory={agent2QueueHistory} />
            </Suspense>
          </div>
        );

      case 'base':
        return (
          <div className="admin-screen animate-fade-in">
            <Suspense fallback={<div className="client-dashboard__loading"><Loader2 size={32} className="animate-spin" /></div>}>
              <AdminLeadsTable
                status={status}
                results={activeResults}
                filteredResults={filteredResults}
                leadSelection={leadSelection} resultsLoading={resultsLoading}
                availableCategories={availableCategories}
                availableSources={availableSources}
                availableResultStates={availableResultStates}
                onClearResults={handleClearResults}
              />
            </Suspense>
          </div>
        );

      case 'requests':
        return (
          <div className="admin-screen animate-fade-in">
            <AdminRequests onStartExtractionFromRequest={handleStartExtractionFromRequest} />
          </div>
        );

      case 'settings':
        return (
          <div className="admin-screen animate-fade-in">
            <Suspense fallback={<div className="client-dashboard__loading"><Loader2 size={32} className="animate-spin" /></div>}>
              <UserProfileSettings isAdmin={true} />
            </Suspense>
          </div>
        );

      case 'clients':
        return (
          <div className="admin-screen animate-fade-in">
            <AdminClientsTable />
          </div>
        );

      case 'ai-agents':
        return (
          <div className="admin-screen animate-fade-in">
            <Suspense fallback={<div className="client-dashboard__loading"><Loader2 size={32} className="animate-spin" /></div>}>
              <AIAgentsCenter
                extractSocials={extractSocials} setExtractSocials={setExtractSocials}
                enableAgent3={enableAgent3} setEnableAgent3={setEnableAgent3}
                agent2Running={agent2Running}
                agent2Status={agent2Status}
                agent2QueueHistory={agent2QueueHistory}
                startAgent2Queue={startAgent2Queue}
                stopAgent2Queue={stopAgent2Queue}
                fetchAgent2QueueHistory={fetchAgent2QueueHistory}
                agent3History={agent3History}
                agent3Status={agent3Status}
                agent3Error={agent3Error}
                fetchAgent3History={fetchAgent3History}
                startAgent3Queue={startAgent3Queue}
                stopAgent3Queue={stopAgent3Queue}
              />
            </Suspense>
          </div>
        );

      default:
        return (
          <div className="admin-screen animate-fade-in">
            <AdminDashboardOverview results={activeResults} excludedResults={excludedResults} status={status} />
          </div>
        );
    }
  };

  return (
    <div className="admin-portal">
      {/* ── Admin Sidebar (Left Navigation) ─────── */}
      <AdminSidebar
        sidebarExpanded={sidebarExpanded}
        toggleSidebar={toggleSidebar}
        activeDashboardSection={activeDashboardSection}
        setActiveDashboardSection={setActiveDashboardSection}
        handleManualSync={handleManualSync}
        theme={theme}
        toggleTheme={toggleTheme}
        handleLogout={handleLogout}
      />

      {/* ── Screen Content ──── */}
      <main className="admin-main">
        {renderAdminScreen()}
      </main>

      <Modal open={!!baseValidationReport} onClose={() => setBaseValidationReport(null)}>
        <h3 style={{ color: 'var(--accent)', marginBottom: '1rem' }}>Relatório de Validação da Base</h3>
        <ValidationReportModal report={baseValidationReport} />
      </Modal>

      <Modal open={!!confirmModal} onClose={() => setConfirmModal(null)}>
        {confirmModal && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#e74c3c' }}>Confirmação</h3>
            <p style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={() => setConfirmModal(null)}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#fff', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button 
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(null);
                }}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#e74c3c', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Confirmar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Contract/Terms Modal */}
      {loggedIn && !isAdmin && userProfile && userProfile.termsAccepted !== true && (
        <ContractModal open={true} />
      )}
    </div>
  );
}
